import React, { useState, useRef, useEffect } from "react";
import { Box } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { useLocation } from "react-router-dom";
import { useTenant } from "../contexts/TenantContext";
import {
  fetchClasses,
  fetchInstructorCourses,
  fetchRegistrationsForClass,
  fetchCourseRoster,
  fetchMessagingThreads,
  createBroadcastThread,
  createDmThread,
  createStaffDmThread,
  createGroupThread,
  sendMessagingMessage,
  fetchMessagingDirectory,
  markThreadRead,
  uploadMessageAttachment,
  fetchAttachmentDownloadUrl,
  unpinThreadMessage,
} from "../api/apiClient";
import ThreadContextPanel from "./messaging/ThreadContextPanel";
import { categoryColor } from "./messaging/categoryColor";

// ── Design tokens ─────────────────────────────────────────────────────────────
const T = {
  ink: "var(--color-ink)", ink2: "var(--color-ink-2)", ink3: "var(--color-ink-3)", ink4: "var(--color-ink-4)",
  line: "var(--color-hairline-cloud)", line2: "var(--color-surface-press-light)", line3: "var(--color-surface-2)",
  surface: "#FFFFFF", surface2: "var(--color-surface-2)", surface3: "var(--color-surface-press-light)",
  teal: "#0D9488", teal50: "rgba(13,148,136,.10)", teal100: "rgba(13,148,136,.22)",
  blue: "var(--color-primary)", blue50: "rgba(37,99,235,.10)", blue100: "rgba(37,99,235,.22)",
  shadowSm: "0 1px 0 rgba(20,24,40,.04), 0 1px 3px rgba(20,24,40,.06)",
  shadowMd: "0 8px 24px -10px rgba(20,24,40,.16)",
  shadowLg: "0 24px 60px -16px rgba(20,24,40,.35)",
};
function makeT(theme) {
  const p = theme.palette.primary.main;
  const s = theme.palette.secondary.main;
  const a = theme.palette.warning.main;
  return {
    ...T,
    primary: p,
    primary700: theme.palette.primary.dark,
    primary10: alpha(p, 0.06),
    primary15: alpha(p, 0.10),
    primary50: alpha(p, 0.15),
    green: s,
    green50: alpha(s, 0.10),
    amber: a,
    amber50: alpha(a, 0.10),
    amber100: alpha(a, 0.22),
    amber900: theme.palette.warning.dark,
  };
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function initials(name) {
  return (name || "").split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]?.toUpperCase()).join("");
}
function roleColor(r, t) {
  return r === "instructor" ? t.amber : r === "coordinator" ? T.blue : t.primary;
}
function roleBg(r, t) {
  return r === "instructor" ? t.amber50 : r === "coordinator" ? T.blue50 : t.primary10;
}
function roleBorder(r, t) {
  return r === "instructor" ? t.amber100 : r === "coordinator" ? T.blue100 : t.primary50;
}
function cap(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : ""; }

// ── Icons ─────────────────────────────────────────────────────────────────────
const MegaphoneIcon = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 11l18-7v16L3 13v-2z"/><path d="M7 13v6a2 2 0 0 0 2 2h1v-7"/></svg>;
const PeopleIcon = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
const SearchIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>;
const PencilIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg>;
const ClockIcon = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
const CheckIcon = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>;
const CheckCheckIcon = () => <svg width="14" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 6 7 17 2 12"/><polyline points="22 6 11 17 9.5 15.5"/></svg>;
const PaperclipIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>;
const SendIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>;
const PanelIcon = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="15" y1="3" x2="15" y2="21"/></svg>;
const BackIcon = () => <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>;
const InfoIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>;
const XIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const ChevronRightIcon = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>;
const PinIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 17v5"/><path d="M5 17h14"/><path d="M7 9V2h10v7l3 4v4H4v-4l3-4z"/></svg>;

const INBOX_FILTERS = ["All", "Unread", "Classes", "Direct"];
const QUICK_REPLIES = ["Thanks!", "Got it, see you at the session.", "Can you clarify?"];

// (no staged data — all data loaded from API on mount)

// ── Small components ──────────────────────────────────────────────────────────
function AvatarEl({ name, role, size = 36 }) {
  const T = makeT(useTheme());
  return (
    <Box sx={{
      width: size, height: size, borderRadius: "50%", flexShrink: 0,
      display: "grid", placeItems: "center",
      bgcolor: roleColor(role, T), color: "#fff",
      fontSize: Math.round(size * 0.34), fontWeight: 700, letterSpacing: "-.01em",
    }}>
      {initials(name)}
    </Box>
  );
}

function RoleBadge({ role }) {
  const T = makeT(useTheme());
  return (
    <Box component="span" sx={{
      fontSize: 10, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase",
      px: "6px", py: "3px", borderRadius: "var(--radius-xs)",
      display: "inline-flex", alignItems: "center", gap: "3px",
      whiteSpace: "nowrap", lineHeight: 1.5, flexShrink: 0,
      bgcolor: roleBg(role, T), color: roleColor(role, T), border: `1px solid ${roleBorder(role, T)}`,
    }}>
      {cap(role)}
    </Box>
  );
}

function IconBtn({ onClick, active, title, children }) {
  const T = makeT(useTheme());
  return (
    <Box component="button" onClick={onClick} title={title} sx={{
      width: 34, height: 34, borderRadius: "9px", flexShrink: 0,
      border: active ? `1.5px solid ${T.primary50}` : `1.5px solid ${T.line}`,
      bgcolor: active ? T.primary10 : "#fff",
      color: active ? T.primary : T.ink3,
      display: "grid", placeItems: "center", cursor: "pointer",
      transition: "all .12s", fontFamily: "inherit",
      "&:hover": { bgcolor: active ? T.primary10 : T.surface3, color: active ? T.primary : T.ink },
    }}>
      {children}
    </Box>
  );
}

// ── Thread row ────────────────────────────────────────────────────────────────
function ThreadRow({ thread, isActive, onClick }) {
  const T = makeT(useTheme());
  const lastMsg = thread.messages[thread.messages.length - 1];
  const preview = lastMsg
    ? (thread.kind === "group" && !lastMsg.own
      ? `${lastMsg.sender.split(" ")[0]}: ${lastMsg.text}`
      : lastMsg.text)
    : "";

  const members = thread.memberCount != null ? `${thread.memberCount} member${thread.memberCount !== 1 ? "s" : ""}` : null;
  const subtitle = thread.kind === "broadcast"
    ? [thread.classCode, members].filter(Boolean).join(" · ")
    : thread.kind === "dm"
      ? [cap(thread.role), thread.classCode].filter(Boolean).join(" · ")
      : members;

  const iconEl = thread.kind === "broadcast"
    ? <Box data-testid="channel-icon" sx={{
        width: 38, height: 38, borderRadius: "10px", bgcolor: T.primary, display: "grid", placeItems: "center", color: "#fff",
        flexShrink: 0, fontFamily: "var(--font-mono)", fontWeight: 600, fontSize: 16, position: "relative",
        "&::after": {
          content: '""', position: "absolute", right: -2, bottom: -2, width: 11, height: 11, borderRadius: "50%",
          bgcolor: categoryColor(thread.category), border: "2px solid #fbfafc",
        },
      }}>#</Box>
    : thread.kind === "group"
    ? <Box sx={{ width: 38, height: 38, borderRadius: "12px", bgcolor: T.green, display: "grid", placeItems: "center", color: "#fff", flexShrink: 0 }}><PeopleIcon /></Box>
    : <AvatarEl name={thread.title} role={thread.role} size={38} />;

  return (
    <Box onClick={onClick} sx={{
      display: "flex", alignItems: "flex-start", gap: "10px",
      mx: "8px", px: "8px", py: "10px", cursor: "pointer", position: "relative",
      borderRadius: "var(--radius-lg)",
      bgcolor: isActive ? "#fff" : "transparent",
      boxShadow: isActive ? `0 0 0 1px ${T.line}, 0 2px 6px -2px rgba(20,16,40,.08)` : "none",
      transition: "background .12s",
      "&:hover": { bgcolor: isActive ? "#fff" : T.surface3 },
    }}>
      {iconEl}
      <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
        {thread.kind !== "dm" && (
          <Box sx={{
            fontSize: "10px", fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase",
            mb: "1px", display: "flex", alignItems: "center", gap: "4px",
            color: thread.kind === "broadcast" ? T.teal : T.green,
          }}>
            {thread.kind === "broadcast" ? <MegaphoneIcon /> : <PeopleIcon />}
            {thread.kind === "broadcast" ? "Broadcast" : "Group"}
          </Box>
        )}
        <Box sx={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
          <Box sx={{
            fontSize: "13.5px", fontWeight: thread.unread > 0 ? 700 : 600,
            color: T.ink, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {thread.title}
          </Box>
          {lastMsg && <Box sx={{ fontSize: "11px", color: T.ink4, flexShrink: 0, whiteSpace: "nowrap", fontFamily: "var(--font-mono)" }}>{lastMsg.time}</Box>}
        </Box>
        <Box sx={{
          fontSize: "12.5px",
          color: thread.unread > 0 ? T.ink2 : T.ink3,
          fontWeight: thread.unread > 0 ? 500 : 400,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", mt: "1px",
        }}>
          {preview}
        </Box>
        {subtitle && (
          <Box sx={{ fontSize: "11.5px", color: T.ink4, mt: "1px", fontFamily: thread.kind === "broadcast" ? "var(--font-mono)" : "inherit", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {subtitle}
          </Box>
        )}
        {(thread.unread > 0 || thread.warning) && (
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mt: "3px" }}>
            {thread.unread > 0 && (
              <Box sx={{
                bgcolor: "var(--color-accent-violet)", color: "#fff", fontSize: "10.5px", fontWeight: 700,
                minWidth: 18, height: 18, px: "5px", borderRadius: "9px",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
              }}>
                {thread.unread}
              </Box>
            )}
            {thread.warning && (
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: "3px", fontSize: "10.5px", fontWeight: 600, color: T.amber }}>
                <ClockIcon /> Deletes in {thread.deleteDays}d
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

// ── Context panel content ─────────────────────────────────────────────────────
function ContextBody({ title, classEnd, deleteDate, deleteDays, participants, participantsCount }) {
  const T = makeT(useTheme());
  const extra = participantsCount && participantsCount > participants.length
    ? participantsCount - participants.length : 0;
  return (
    <>
      <Box sx={{ border: `1.5px solid ${T.line2}`, borderRadius: "14px", p: "14px 15px" }}>
        <Box sx={{ fontSize: "10.5px", fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: T.ink4, mb: "6px" }}>Class</Box>
        <Box sx={{ fontSize: "14.5px", fontWeight: 800, color: T.ink, letterSpacing: "-.01em", fontFamily: "var(--font-display)" }}>{title}</Box>
        {classEnd && (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", mt: "12px", fontSize: "12.5px" }}>
            <Box sx={{ color: T.ink3, fontWeight: 500 }}>Class ends</Box>
            <Box sx={{ fontWeight: 700, color: T.ink }}>{classEnd}</Box>
          </Box>
        )}
        {deleteDate && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", mt: "10px", pt: "10px", borderTop: `1px solid ${T.line2}`, fontSize: "12.5px" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "6px", color: T.ink3, fontWeight: 500 }}><ClockIcon /> Thread deletes</Box>
              <Box sx={{
                fontSize: "11px", fontWeight: 700, px: "9px", py: "3px", borderRadius: "999px",
                display: "inline-flex", alignItems: "center", gap: "4px", whiteSpace: "nowrap",
                bgcolor: deleteDays <= 7 ? T.amber50 : T.surface3,
                color: deleteDays <= 7 ? T.amber : T.ink3,
                border: `1px solid ${deleteDays <= 7 ? T.amber100 : T.line2}`,
              }}>
                {deleteDays <= 7 && <ClockIcon />} {deleteDays} day{deleteDays !== 1 ? "s" : ""}
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", pt: "2px", fontSize: "11px" }}>
              <Box sx={{ color: T.ink4, fontWeight: 500 }}>7 days after class ends</Box>
              <Box sx={{ color: T.ink4, fontWeight: 600 }}>{deleteDate}</Box>
            </Box>
          </>
        )}
      </Box>
      <Box>
        <Box sx={{ fontSize: "10.5px", fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: T.ink4 }}>
          Participants ({participantsCount || participants.length})
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", mt: "8px" }}>
          {participants.map((p, i) => (
            <Box key={i} sx={{ display: "flex", alignItems: "center", gap: "9px", px: "4px", py: "6px", borderRadius: "9px", "&:hover": { bgcolor: T.surface2 } }}>
              <AvatarEl name={p.name} role={p.role} size={28} />
              <Box sx={{ fontSize: "13px", fontWeight: 600, color: T.ink, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</Box>
              <RoleBadge role={p.role} />
            </Box>
          ))}
          {extra > 0 && <Box sx={{ fontSize: "12px", color: T.ink4, fontWeight: 600, px: "4px", py: "6px" }}>+ {extra} more</Box>}
        </Box>
      </Box>
    </>
  );
}

// ── Modal wrapper ─────────────────────────────────────────────────────────────
function Modal({ show, onClose, children, maxWidth = 340 }) {
  if (!show) return null;
  return (
    <Box onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} sx={{
      position: "fixed", inset: 0, bgcolor: "rgba(20,24,40,.4)",
      backdropFilter: "blur(3px)", zIndex: 200,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <Box sx={{ bgcolor: "#fff", borderRadius: "18px", border: `1px solid ${T.line}`, boxShadow: T.shadowLg, p: "22px", maxWidth, width: "100%", mx: "16px" }}>
        {children}
      </Box>
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function MessagingPage() {
  const T = makeT(useTheme());
  const { user } = useTenant();
  const location = useLocation();
  const initialThreadId = location.state?.activeThreadId || null;
  const meRole = user?.roles?.includes("INSTRUCTOR") ? "instructor"
    : user?.roles?.includes("COURSE_COORDINATOR") ? "coordinator"
    : user?.roles?.includes("ADMIN") || user?.roles?.includes("GLOBAL_ADMIN") ? "coordinator"
    : "student";
  const me = { name: user?.fullName || user?.username || "Me", role: meRole };

  const [threads, setThreads] = useState({});
  const [order, setOrder] = useState({ broadcast: [], dm: [], group: [] });
  const [directory, setDirectory] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [contextCollapsed, setContextCollapsed] = useState(false);
  const [showNewMsg, setShowNewMsg] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [mobileView, setMobileView] = useState("list");
  const [searchQ, setSearchQ] = useState("");
  const [inboxFilter, setInboxFilter] = useState("All");
  const [nmSearch, setNmSearch] = useState("");
  const [nmTab, setNmTab] = useState("dm");
  const [composeText, setComposeText] = useState("");
  const [pinToDashboard, setPinToDashboard] = useState(false);
  const [announce, setAnnounce] = useState(false);
  const [classes, setClasses] = useState([]);
  const [classParticipants, setClassParticipants] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [broadcastSending, setBroadcastSending] = useState(false);
  const [broadcastError, setBroadcastError] = useState(null);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [msgClassStep, setMsgClassStep] = useState(null);
  const [rosterCache, setRosterCache] = useState({});
  const [rosterSelected, setRosterSelected] = useState(() => new Set());
  const [rosterLoading, setRosterLoading] = useState(false);
  const [rosterError, setRosterError] = useState(null);
  const [groupCreating, setGroupCreating] = useState(false);
  const [groupError, setGroupError] = useState(null);
  const [dmError, setDmError] = useState(null);

  // Load threads and directory from API
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [threadsData, dirData] = await Promise.allSettled([
          fetchMessagingThreads(),
          fetchMessagingDirectory(),
        ]);
        if (cancelled) return;
        if (threadsData.status === "fulfilled") {
          const data = threadsData.value;
          setThreads(data.threads || {});
          setOrder(data.order || { broadcast: [], dm: [], group: [] });
          const allIds = [
            ...(data.order?.broadcast || []),
            ...(data.order?.dm || []),
            ...(data.order?.group || []),
          ];
          const targetId = initialThreadId && data.threads?.[initialThreadId]
            ? initialThreadId
            : allIds.length > 0 ? allIds[0] : null;
          if (targetId) {
            setActiveId(targetId);
            if (initialThreadId && targetId === initialThreadId) {
              setMobileView("thread");
              markThreadRead(targetId).catch(() => {});
            }
          }
        } else {
          if (!cancelled) setLoadError(true);
        }
        if (dirData.status === "fulfilled") {
          setDirectory(dirData.value || []);
        }
      } catch (_) {
        if (!cancelled) setLoadError(true);
      }
      if (!cancelled) setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [initialThreadId]);

  // Load instructor/coordinator's classes (for broadcast + message-by-class)
  useEffect(() => {
    if (meRole === "student" || !user?.id) return;
    let cancelled = false;
    const source = meRole === "instructor"
      ? fetchInstructorCourses(user.id, user.tenantId)
      : fetchClasses(user.tenantId);
    source.then(courses => {
      if (cancelled) return;
      const list = Array.isArray(courses) ? courses : [];
      setClasses(list.map(c => ({
        id: String(c.id),
        name: c.title || c.name || `Course ${c.id}`,
        studentCount: c.studentCount || 0,
        endDate: null,
        deleteDate: null,
        threadId: null,
      })));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [meRole, user?.id, user?.tenantId]);

  const messagesRef = useRef(null);
  const composeRef = useRef(null);
  const sendingRef = React.useRef(false);
  const fileInputRef = useRef(null);

  const activeThread = threads[activeId] || null;

  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [activeId, threads]);

  // Fetch students for broadcast threads so context panel shows full roster
  useEffect(() => {
    const t = threads[activeId];
    if (!t || t.kind !== "broadcast" || !t.classId) return;
    if (classParticipants[t.classId]) return; // already loaded
    let cancelled = false;
    fetchRegistrationsForClass(t.classId).then(regs => {
      if (cancelled) return;
      const list = Array.isArray(regs) ? regs : [];
      const students = list.map(r => ({
        name: r.name || r.studentName || r.fullName || `Student ${r.userId}`,
        role: "student",
      }));
      setClassParticipants(prev => ({ ...prev, [t.classId]: students }));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [activeId, threads, classParticipants]);

  // Restore active thread from URL hash after threads load
  useEffect(() => {
    if (loading) return;
    const hash = window.location.hash.replace("#thread-", "");
    if (hash && threads[hash] && !activeId) setActiveId(hash);
  }, [loading, activeId, threads]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const selectThread = (id) => {
    setPendingAttachments([]);
    setThreads(prev => {
      const t = prev[id];
      if (t?.unread) return { ...prev, [id]: { ...t, unread: 0 } };
      return prev;
    });
    setActiveId(id);
    setComposeText("");
    setPinToDashboard(false);
    setAnnounce(false);
    setMobileView("thread");
    window.history.replaceState(null, "", `#thread-${id}`);
    if (threads[id]?.unread) markThreadRead(id).catch(() => console.warn("markThreadRead failed"));
  };

  const startConversation = async (personId) => {
    const p = directory.find(x => x.id === personId);
    if (!p) return;
    setDmError(null);
    if (p.threadId && threads[p.threadId]) {
      setShowNewMsg(false);
      selectThread(p.threadId);
      return;
    }
    try {
      const thread = p.sharedClassId
        ? await createDmThread(Number(p.sharedClassId), Number(personId))
        : await createStaffDmThread(Number(personId));
      const newId = thread.id;
      setThreads(prev => ({ ...prev, [newId]: thread }));
      setOrder(prev => ({ ...prev, dm: [newId, ...prev.dm.filter(id => id !== newId)] }));
      setDirectory(prev => prev.map(p => p.id === personId ? { ...p, threadId: newId } : p));
      setShowNewMsg(false);
      selectThread(newId);
    } catch (err) {
      console.error("Failed to create DM thread:", err);
      setDmError("Couldn't start that conversation. Please try again.");
    }
  };

  const startBroadcast = async (classId) => {
    const cls = classes.find(c => c.id === classId);
    if (!cls || broadcastSending) return;

    if (cls.threadId && threads[cls.threadId]) {
      setShowNewMsg(false);
      selectThread(cls.threadId);
      return;
    }

    setBroadcastSending(true);
    setBroadcastError(null);
    try {
      const threadData = await createBroadcastThread({
        classId: Number(cls.id),
        classTitle: cls.name,
        studentCount: cls.studentCount,
      });
      const newId = threadData.id;
      setThreads(prev => ({ ...prev, [newId]: threadData }));
      setOrder(prev => ({ ...prev, broadcast: [newId, ...prev.broadcast.filter(id => id !== newId)] }));
      setClasses(prev => prev.map(c => c.id === classId ? { ...c, threadId: newId } : c));
      setShowNewMsg(false);
      selectThread(newId);
    } catch (err) {
      console.error("Failed to create broadcast thread:", err);
      setBroadcastError("Failed to create broadcast. Please try again.");
    } finally {
      setBroadcastSending(false);
    }
  };

  const pickClassForMessage = async (classId) => {
    setMsgClassStep(classId);
    setRosterSelected(new Set());
    setGroupError(null);
    setRosterError(null);
    if (rosterCache[classId]) return;
    setRosterLoading(true);
    try {
      const roster = await fetchCourseRoster(Number(classId));
      setRosterCache(prev => ({ ...prev, [classId]: Array.isArray(roster) ? roster : [] }));
    } catch (err) {
      console.error("Failed to load class roster:", err);
      setRosterError(
        err?.response?.status === 403
          ? "You don't have access to this class's roster."
          : "Failed to load class roster. Please try again."
      );
    } finally {
      setRosterLoading(false);
    }
  };

  const toggleRosterStudent = (id) => {
    setRosterSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const confirmClassSelection = async () => {
    const selectedIds = Array.from(rosterSelected);
    if (selectedIds.length === 0 || groupCreating) return;
    setGroupCreating(true);
    setGroupError(null);
    try {
      if (selectedIds.length === 1) {
        const thread = await createDmThread(Number(msgClassStep), Number(selectedIds[0]));
        const newId = thread.id;
        setThreads(prev => ({ ...prev, [newId]: thread }));
        setOrder(prev => ({ ...prev, dm: [newId, ...prev.dm.filter(id => id !== newId)] }));
        closeNewMessage();
        selectThread(newId);
      } else {
        const thread = await createGroupThread({ participantIds: selectedIds.map(Number) });
        const newId = thread.id;
        setThreads(prev => ({ ...prev, [newId]: thread }));
        setOrder(prev => ({ ...prev, group: [newId, ...prev.group.filter(id => id !== newId)] }));
        closeNewMessage();
        selectThread(newId);
      }
    } catch (err) {
      console.error("Failed to start conversation:", err);
      setGroupError("Failed to start conversation. Please try again.");
    } finally {
      setGroupCreating(false);
    }
  };

  const closeNewMessage = () => {
    setShowNewMsg(false);
    setNmTab("dm");
    setMsgClassStep(null);
    setRosterSelected(new Set());
    setGroupError(null);
    setRosterError(null);
    setDmError(null);
  };

  const handlePickFiles = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!activeThread || !files.length) return;

    const MAX_PER_MESSAGE = 5;
    const remaining = MAX_PER_MESSAGE - pendingAttachments.length;
    const toUpload = files.slice(0, remaining);

    const placeholders = toUpload.map((file) => ({
      localId: Math.random().toString(36).slice(2),
      file,
      uploadId: null,
      progress: 0,
      error: null,
    }));
    setPendingAttachments((prev) => [...prev, ...placeholders]);

    for (const ph of placeholders) {
      try {
        const result = await uploadMessageAttachment(
          activeThread.id,
          ph.file,
          (loaded, total) => {
            const pct = total ? Math.round((loaded / total) * 100) : 50;
            setPendingAttachments((prev) =>
              prev.map((p) => (p.localId === ph.localId ? { ...p, progress: pct } : p))
            );
          }
        );
        setPendingAttachments((prev) =>
          prev.map((p) =>
            p.localId === ph.localId ? { ...p, uploadId: result.id, progress: 100 } : p
          )
        );
      } catch {
        setPendingAttachments((prev) =>
          prev.map((p) =>
            p.localId === ph.localId ? { ...p, error: "Upload failed" } : p
          )
        );
      }
    }
  };

  const removePendingAttachment = (localId) => {
    setPendingAttachments((prev) => prev.filter((p) => p.localId !== localId));
  };

  const sendMessage = async () => {
    if (sendingRef.current) return;
    const text = composeText.trim();
    const readyAttachments = pendingAttachments.filter((p) => p.uploadId && !p.error);
    if (!text && readyAttachments.length === 0) return;
    sendingRef.current = true;

    const t = activeThread;
    if (!t) { sendingRef.current = false; return; }

    const attachmentIds = readyAttachments.map((p) => Number(p.uploadId));
    try {
      const asAnnouncement = t.kind === "broadcast" && announce;
      const savedMsg = await sendMessagingMessage(t.id, text, attachmentIds, {
        pinToDashboard: asAnnouncement && pinToDashboard,
        ...(t.kind === "broadcast" && meRole !== "student" && { type: asAnnouncement ? "announcement" : "message" }),
      });
      setThreads(prev => ({
        ...prev,
        [t.id]: {
          ...t,
          messages: [...t.messages.map(m => savedMsg.pinned ? { ...m, pinned: false } : m), savedMsg],
        },
      }));
      setComposeText("");
      setPinToDashboard(false);
      setAnnounce(false);
      setPendingAttachments([]);
      if (composeRef.current) composeRef.current.style.height = "42px";
    } catch (err) {
      console.error("Failed to send message:", err);
    } finally {
      sendingRef.current = false;
    }
  };

  const handleUnpin = async (threadId) => {
    try {
      await unpinThreadMessage(threadId);
      setThreads(prev => {
        const t = prev[threadId];
        if (!t) return prev;
        return {
          ...prev,
          [threadId]: {
            ...t,
            messages: t.messages.map(m => m.pinned ? { ...m, pinned: false } : m),
          },
        };
      });
    } catch (err) {
      console.error("Failed to unpin message:", err);
    }
  };

  const handleComposeKeydown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      const hasAttachments = pendingAttachments.some((p) => p.uploadId && !p.error);
      if (composeText.trim() || hasAttachments) sendMessage();
    }
  };

  const handleComposeChange = (e) => {
    setComposeText(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
  };

  // ── Filtered thread list ───────────────────────────────────────────────────
  const q = searchQ.trim().toLowerCase();
  const matchThread = (t) => !q || t.title.toLowerCase().includes(q) ||
    (t.messages.at(-1)?.text || "").toLowerCase().includes(q);

  const matchFilter = (t) => inboxFilter === "All"
    || (inboxFilter === "Unread" && t.unread > 0)
    || (inboxFilter === "Classes" && (t.kind === "broadcast" || t.kind === "group"))
    || (inboxFilter === "Direct" && t.kind === "dm");
  const visibleThreads = (ids) => ids.map(id => threads[id]).filter(Boolean).filter(matchThread).filter(matchFilter);
  const broadcastThreads = visibleThreads(order.broadcast);
  const dmThreads = visibleThreads(order.dm);
  const groupThreads = visibleThreads(order.group);
  const unreadThreadCount = Object.values(threads).filter(t => t.unread > 0).length;

  // ── Filtered directory ─────────────────────────────────────────────────────
  const nq = nmSearch.trim().toLowerCase();
  const matchPerson = (p) => !nq || p.name.toLowerCase().includes(nq) || p.context.toLowerCase().includes(nq);
  const dirByRole = ["instructor", "coordinator", "student"].map(role => ({
    role, label: role === "instructor" ? "Instructors" : role === "coordinator" ? "Coordinators" : "Classmates",
    people: directory.filter(p => p.role === role && matchPerson(p)),
  })).filter(g => g.people.length > 0);

  // ── Renders ────────────────────────────────────────────────────────────────
  const renderContextContent = () => {
    if (!activeThread) return null;
    // For broadcast threads, show instructor(s) + all fetched students
    const participants = activeThread.kind === "broadcast" && activeThread.classId
      ? [
          ...activeThread.participants,
          ...(classParticipants[activeThread.classId] || []),
        ]
      : activeThread.participants;
    const participantsCount = activeThread.kind === "broadcast"
      ? participants.length || activeThread.participantsCount
      : activeThread.participantsCount;
    const body = (
      <ContextBody
        title={activeThread.title}
        classEnd={activeThread.classEnd}
        deleteDate={activeThread.deleteDate}
        deleteDays={activeThread.deleteDays}
        participants={participants}
        participantsCount={participantsCount}
      />
    );
    return (
      <ThreadContextPanel
        thread={activeThread}
        isStaff={meRole !== "student"}
        Avatar={AvatarEl}
        RoleBadge={RoleBadge}
        fallback={body}
      />
    );
  };

  const renderMessages = () => {
    const msgs = activeThread?.messages || [];
    if (!msgs.length) {
      const isBroadcast = activeThread?.kind === "broadcast";
      const iconEl = isBroadcast
        ? <Box sx={{ width: 64, height: 64, borderRadius: "18px", bgcolor: T.teal, display: "grid", placeItems: "center", color: "#fff", flexShrink: 0 }}><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 11l18-7v16L3 13v-2z"/><path d="M7 13v6a2 2 0 0 0 2 2h1v-7"/></svg></Box>
        : <AvatarEl name={activeThread?.title} role={activeThread?.role} size={64} />;
      const headline = isBroadcast
        ? `No messages sent to ${activeThread.title} yet`
        : `No messages yet with ${activeThread.title}`;
      return (
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "12px", p: "40px 32px", textAlign: "center" }}>
          {iconEl}
          <Box sx={{ fontSize: "17px", fontWeight: 800, letterSpacing: "-.02em", color: T.ink, fontFamily: "var(--font-display)" }}>{headline}</Box>
          <Box sx={{ fontSize: "13.5px", color: T.ink3, maxWidth: 360, lineHeight: 1.6 }}>
            {isBroadcast ? "Compose a message below to broadcast to all students in this class." : "Send a message to start the conversation."}
          </Box>
        </Box>
      );
    }

    let lastDay = null;
    let lastSender = null;
    const isBroadcast = activeThread.kind === "broadcast";
    return msgs.map((m) => {
      const type = m.type || (isBroadcast ? "announcement" : "message");
      const isBroadcastThread = type === "announcement";
      const isEvent = type === "event";
      const showDivider = m.day !== lastDay;
      lastDay = m.day;
      const grouped = !showDivider && !isBroadcastThread && !isEvent && lastSender === m.sender;
      lastSender = isBroadcastThread || isEvent ? null : m.sender;
      const ownBubble = m.own && !isBroadcastThread;
      const isLast = m === msgs[msgs.length - 1];
      const receipt = m.own
        ? isBroadcastThread
          ? <Box sx={{ fontSize: "11.5px", color: m.seenCount === m.total ? T.green : T.ink4, fontWeight: m.seenCount === m.total ? 600 : 400, mt: "4px", display: "flex", alignItems: "center", gap: "5px" }}><CheckCheckIcon /> Seen by {m.seenCount} of {m.total} students</Box>
          : isLast && activeThread.kind === "dm"
            ? m.seen
              ? <Box sx={{ fontSize: "11.5px", color: T.green, fontWeight: 600, mt: "4px", display: "flex", alignItems: "center", gap: "5px" }}><CheckCheckIcon /> Read {m.seenTime}</Box>
              : <Box sx={{ fontSize: "11.5px", color: T.ink4, mt: "4px", display: "flex", alignItems: "center", gap: "5px" }}><CheckIcon /> Delivered</Box>
            : null
        : null;

      if (isEvent) {
        return (
          <React.Fragment key={m.id}>
            {showDivider && <DayDivider day={m.day} />}
            <Box data-testid="event" sx={{
              display: "flex", alignItems: "center", gap: "8px", justifyContent: "center", fontSize: "12.5px",
              opacity: 0.7, mx: "auto", my: "10px", px: "12px", py: "6px", borderRadius: "999px",
              bgcolor: "#f7f6f9", maxWidth: "calc(100% - 32px)", textAlign: "center",
            }}>
              <InfoIcon />
              <span>{m.text}</span>
              <Box component="time" sx={{ fontFamily: "var(--font-mono)", fontSize: 11, opacity: 0.6 }}>{m.time}</Box>
            </Box>
          </React.Fragment>
        );
      }

      return (
        <React.Fragment key={m.id}>
          {showDivider && <DayDivider day={m.day} />}
          <Box data-testid={isBroadcastThread ? "announcement" : m.own ? "message-own" : "message"} sx={{
            display: "flex", gap: "10px", px: { xs: "16px", md: "24px" }, pt: grouped ? "2px" : "10px", pb: "2px",
            flexDirection: ownBubble ? "row-reverse" : "row",
          }}>
            {!ownBubble && (grouped ? <Box sx={{ width: 32, flexShrink: 0 }} /> : <AvatarEl name={m.sender} role={m.role} size={32} />)}
            <Box sx={{
              minWidth: 0, maxWidth: 640,
              ...(isBroadcastThread
                ? { flex: 1, border: `1px solid ${T.line}`, borderRadius: "12px", p: "14px 16px", bgcolor: "#fff" }
                : { display: "flex", flexDirection: "column", alignItems: ownBubble ? "flex-end" : "flex-start" }),
            }}>
              {isBroadcastThread && (
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mb: "6px", color: T.primary }}>
                  <MegaphoneIcon />
                  <Box sx={{ fontSize: 10, fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase" }}>Announcement</Box>
                </Box>
              )}
              {!grouped && !ownBubble && (
              <Box sx={{ display: "flex", alignItems: "baseline", gap: "8px", flexWrap: "wrap" }}>
                <Box sx={{ fontSize: "13.5px", fontWeight: 700, color: T.ink }}>{m.sender}</Box>
                <RoleBadge role={m.role} />
                {m.pinned && (
                  <Box sx={{ display: "inline-flex", alignItems: "center", gap: "4px", fontSize: "10.5px", fontWeight: 800, letterSpacing: ".04em", textTransform: "uppercase", color: T.teal, bgcolor: T.teal50, border: `1px solid ${T.teal100}`, borderRadius: "999px", px: "7px", py: "2px" }}>
                    <PinIcon /> Pinned
                  </Box>
                )}
                {m.pinned && (meRole === "instructor" || meRole === "coordinator") && isBroadcast && (
                  <Box
                    component="button"
                    onClick={() => handleUnpin(activeThread.id)}
                    title="Remove this pinned announcement from the class dashboard"
                    sx={{ fontSize: "10.5px", fontWeight: 700, color: T.ink3, bgcolor: "transparent", border: "none", cursor: "pointer", textDecoration: "underline", p: 0, "&:hover": { color: T.ink } }}
                  >
                    Unpin
                  </Box>
                )}
                <Box sx={{ fontSize: "11px", color: T.ink4, fontFamily: "var(--font-mono)" }}>{m.time}</Box>
              </Box>
              )}
              {m.text && (
              <Box sx={isBroadcastThread
                ? { fontSize: "14.5px", fontWeight: 500, color: T.ink, lineHeight: 1.5, mt: "2px", whiteSpace: "pre-wrap", wordBreak: "break-word" }
                : {
                    fontSize: "14px", lineHeight: 1.45, mt: "2px", whiteSpace: "pre-wrap", overflowWrap: "anywhere",
                    p: "9px 13px", borderRadius: ownBubble ? "14px 4px 14px 14px" : grouped ? "14px" : "4px 14px 14px 14px",
                    bgcolor: ownBubble ? T.primary : "#F2F0F6", color: ownBubble ? "#fff" : T.ink,
                  }}>
                {m.text}
              </Box>
              )}
              {m.attachments?.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", mt: m.text ? "8px" : "4px" }}>
                  {m.attachments.map((a) => (
                    <Box
                      key={a.id}
                      component="button"
                      onClick={async () => {
                        try {
                          const url = await fetchAttachmentDownloadUrl(a.id);
                          window.open(url, "_blank", "noopener");
                        } catch {
                          // ignore — user can retry
                        }
                      }}
                      title={`Download ${a.originalFilename}`}
                      sx={{
                        display: "flex", alignItems: "center", gap: "7px",
                        px: "10px", py: "7px", borderRadius: "8px",
                        border: `1.5px solid ${T.line}`, bgcolor: T.surface,
                        cursor: "pointer", fontFamily: "inherit", textAlign: "left",
                        transition: "all .12s", color: T.ink2, fontSize: 12.5,
                        "&:hover": { bgcolor: T.surface2, borderColor: T.primary, color: T.primary },
                      }}>
                      <PaperclipIcon />
                      <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 180 }}>
                        {a.originalFilename}
                      </Box>
                      {a.fileSize != null && (
                        <Box sx={{ color: T.ink4, flexShrink: 0, fontSize: 11 }}>
                          {a.fileSize < 1_048_576
                            ? `${Math.round(a.fileSize / 1024)} KB`
                            : `${(a.fileSize / 1_048_576).toFixed(1)} MB`}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
              {receipt}
            </Box>
          </Box>
        </React.Fragment>
      );
    });
  };

  const renderHeader = () => {
    if (!activeThread) return null;
    const t = activeThread;
    const iconEl = t.kind === "broadcast"
      ? <Box sx={{ width: 40, height: 40, borderRadius: "var(--radius-md)", bgcolor: T.primary, display: "grid", placeItems: "center", color: "#fff", flexShrink: 0, fontFamily: "var(--font-mono)", fontWeight: 600, fontSize: 16 }}>#</Box>
      : t.kind === "group"
      ? <Box sx={{ width: 36, height: 36, borderRadius: "11px", bgcolor: T.green, display: "grid", placeItems: "center", color: "#fff", flexShrink: 0 }}><PeopleIcon /></Box>
      : <AvatarEl name={t.title} role={t.role} size={36} />;
    const title = t.title;
    const sub = t.kind === "broadcast" && t.classCode
      ? `${t.classCode} · ${t.memberCount ?? 0} member${t.memberCount !== 1 ? "s" : ""} · Instructors and coordinators can post announcements`
      : t.subtitle;

    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px", px: { xs: "16px", md: "22px" }, py: "12px", borderBottom: "1px solid rgba(0,0,0,0.07)", flexShrink: 0 }}>
        <Box component="button" onClick={() => setMobileView("list")}
          sx={{ display: { xs: "flex", md: "none" }, width: 32, height: 32, borderRadius: "8px", border: 0, bgcolor: "transparent", color: T.ink2, alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
          <BackIcon />
        </Box>
        {iconEl}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ fontSize: "15px", fontWeight: 800, letterSpacing: "-.01em", color: T.ink, fontFamily: "var(--font-display)" }}>{title}</Box>
          <Box sx={{ fontSize: "12px", color: T.ink3, mt: "1px" }}>{sub}</Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px", flexShrink: 0 }}>
          <Box sx={{ display: { xs: "none", md: "block" } }}>
            <IconBtn onClick={() => setContextCollapsed(c => !c)} active={!contextCollapsed} title="Thread info">
              <PanelIcon />
            </IconBtn>
          </Box>
          <Box sx={{ display: { xs: "block", md: "none" } }}>
            <IconBtn onClick={() => setShowInfoModal(true)} title="Thread info">
              <InfoIcon />
            </IconBtn>
          </Box>
        </Box>
      </Box>
    );
  };

  // ── Layout ─────────────────────────────────────────────────────────────────
  return (
    <Box sx={{
      display: "flex",
      flexDirection: "column",
      width: "100%",
      maxWidth: 1440,
      mx: "auto",
      height: "100%",
      overflow: "hidden",
      bgcolor: T.surface,
      fontFamily: "var(--font-ui)",
      borderLeft: { md: `1px solid ${T.line}` },
      borderRight: { md: `1px solid ${T.line}` },
    }}>

      {/* ══ THREE-PANEL GRID ══ */}
      <Box sx={{
        flex: 1,
        display: "grid",
        gridTemplateColumns: contextCollapsed
          ? { xs: "1fr", md: "340px 1fr 0px" }
          : { xs: "1fr", md: "340px 1fr 300px" },
        minHeight: 0, overflow: "hidden", bgcolor: T.surface,
        transition: "grid-template-columns .18s",
      }}>

        {/* ── LEFT: Thread list ── */}
        <Box sx={{
          borderRight: `1px solid ${T.line}`,
          display: { xs: mobileView === "list" ? "flex" : "none", md: "flex" },
          flexDirection: "column", minHeight: 0, overflow: "hidden", bgcolor: "#fbfafc",
        }}>
          <Box sx={{ px: "16px", pt: "16px", fontSize: "22px", fontWeight: 600, letterSpacing: "-.01em", color: T.ink, fontFamily: "var(--font-display)", flexShrink: 0 }}>Messages</Box>
          {/* Search + new */}
          <Box sx={{ p: "12px 14px 8px", flexShrink: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Box sx={{ position: "relative", flex: 1 }}>
                <Box sx={{ position: "absolute", left: "11px", top: "50%", transform: "translateY(-50%)", color: T.ink4, pointerEvents: "none", display: "flex" }}>
                  <SearchIcon />
                </Box>
                <Box
                  component="input"
                  type="text"
                  placeholder="Search conversations"
                  value={searchQ}
                  onChange={e => setSearchQ(e.target.value)}
                  sx={{
                    width: "100%", height: 38, pl: "34px", pr: "12px",
                    border: `1.5px solid ${T.line}`, borderRadius: "10px",
                    bgcolor: T.surface2, fontFamily: "inherit", fontSize: 13, color: T.ink,
                    outline: "none", transition: "border-color .14s, background .14s",
                    "&:focus": { borderColor: T.primary, bgcolor: "#fff", boxShadow: `0 0 0 3px ${T.primary10}` },
                  }}
                />
              </Box>
              <Box component="button" onClick={() => { setNmSearch(""); setShowNewMsg(true); }} title="New message" sx={{
                width: 38, height: 38, borderRadius: "10px", border: `1.5px solid ${T.line}`,
                bgcolor: "#fff", color: T.ink3, display: "grid", placeItems: "center", cursor: "pointer",
                flexShrink: 0, fontFamily: "inherit", transition: "all .12s",
                "&:hover": { bgcolor: T.primary, color: "#fff", borderColor: T.primary },
              }}>
                <PencilIcon />
              </Box>
              {me.role !== "student" && (
                <Box component="button" onClick={() => { setBroadcastError(null); setShowBroadcastModal(true); }} title="New broadcast" sx={{
                  width: 38, height: 38, borderRadius: "10px", border: `1.5px solid ${T.line}`,
                  bgcolor: "#fff", color: T.ink3, display: "grid", placeItems: "center", cursor: "pointer",
                  flexShrink: 0, fontFamily: "inherit", transition: "all .12s",
                  "&:hover": { bgcolor: T.teal, color: "#fff", borderColor: T.teal },
                }}>
                  <MegaphoneIcon />
                </Box>
              )}
            </Box>
          </Box>

          <Box role="group" aria-label="Filter conversations" sx={{ display: "flex", gap: "4px", px: "14px", pb: "8px", flexShrink: 0, flexWrap: "wrap" }}>
            {INBOX_FILTERS.map(f => (
              <Box key={f} component="button" onClick={() => setInboxFilter(f)} aria-pressed={inboxFilter === f} sx={{
                height: 28, px: "10px", borderRadius: "999px", cursor: "pointer", fontFamily: "inherit",
                fontSize: "12.5px", fontWeight: 500, display: "flex", alignItems: "center", gap: "6px",
                border: `1px solid ${inboxFilter === f ? "transparent" : T.line}`,
                bgcolor: inboxFilter === f ? T.ink : "#fff", color: inboxFilter === f ? "#fff" : T.ink,
              }}>
                {f}
                {f === "Unread" && <Box component="span" sx={{ fontFamily: "var(--font-mono)", fontSize: "10.5px", fontWeight: 600, opacity: 0.7 }}>{unreadThreadCount}</Box>}
              </Box>
            ))}
          </Box>

          {/* Thread groups */}
          <Box sx={{ flex: 1, overflowY: "auto", pb: "10px" }}>
            {broadcastThreads.length > 0 && (
              <>
                <ThreadGroupLabel>Class Broadcasts</ThreadGroupLabel>
                {broadcastThreads.map(t => <ThreadRow key={t.id} thread={t} isActive={activeId === t.id} onClick={() => selectThread(t.id)} />)}
              </>
            )}
            {dmThreads.length > 0 && (
              <>
                <ThreadGroupLabel>Direct Messages</ThreadGroupLabel>
                {dmThreads.map(t => <ThreadRow key={t.id} thread={t} isActive={activeId === t.id} onClick={() => selectThread(t.id)} />)}
              </>
            )}
            {groupThreads.length > 0 && (
              <>
                <ThreadGroupLabel>Class Groups</ThreadGroupLabel>
                {groupThreads.map(t => <ThreadRow key={t.id} thread={t} isActive={activeId === t.id} onClick={() => selectThread(t.id)} />)}
              </>
            )}
            {!broadcastThreads.length && !dmThreads.length && !groupThreads.length && (
              <Box sx={{ p: "40px 20px", textAlign: "center", color: T.ink4, fontSize: 13 }}>
                {searchQ || inboxFilter !== "All" ? "No conversations match." : "No conversations yet."}
              </Box>
            )}
          </Box>
        </Box>

        {/* ── CENTER: Active thread ── */}
        <Box sx={{
          display: { xs: mobileView === "thread" ? "flex" : "none", md: "flex" },
          flexDirection: "column", minHeight: 0, overflow: "hidden", bgcolor: T.surface,
        }}>
          {renderHeader()}

          {/* Ephemeral banner */}
          {activeThread?.warning && (
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: "9px", px: { xs: "16px", md: "22px" }, py: "9px", bgcolor: T.amber50, borderBottom: `1px solid ${T.amber100}`, color: T.amber900, fontSize: "12.5px", fontWeight: 600, flexShrink: 0, lineHeight: 1.5 }}>
              <Box sx={{ display: "flex", flexShrink: 0, mt: "2px" }}><ClockIcon /></Box>
              <Box>This thread <strong>deletes in {activeThread.deleteDays} day{activeThread.deleteDays !== 1 ? "s" : ""}</strong> ({activeThread.deleteDate}) — 7 days after the class ended on {activeThread.classEnd}.</Box>
            </Box>
          )}

          {activeThread ? (
            <>
              {/* Messages */}
              <Box ref={messagesRef} sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", py: "10px", minHeight: 0, display: "flex", flexDirection: "column" }}>
                {renderMessages()}
              </Box>

              {/* Composer */}
              <Box sx={{ borderTop: `1px solid ${T.line2}`, p: { xs: "10px 16px", md: "12px 22px" }, display: "flex", flexDirection: "column", gap: "8px", flexShrink: 0, bgcolor: "#fff" }}>
                {pendingAttachments.length > 0 && (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", pl: "52px" }}>
                    {pendingAttachments.map((p) => (
                      <Box key={p.localId} sx={{
                        display: "flex", alignItems: "center", gap: "6px",
                        px: "10px", py: "5px", borderRadius: "8px",
                        bgcolor: p.error ? "var(--color-danger-bg)" : T.surface2,
                        border: `1px solid ${p.error ? "var(--color-danger-bg)" : T.line}`,
                        fontSize: 12.5, color: p.error ? "var(--color-danger)" : T.ink2, maxWidth: 200,
                      }}>
                        <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                          {p.error ? `${p.file.name} — failed` : p.progress < 100 ? `${p.file.name} ${p.progress}%` : p.file.name}
                        </Box>
                        <Box component="button" onClick={() => removePendingAttachment(p.localId)}
                          sx={{ flexShrink: 0, bgcolor: "transparent", border: "none", color: T.ink4, cursor: "pointer", fontFamily: "inherit", lineHeight: 1, p: 0, fontSize: 14, "&:hover": { color: T.ink } }}>
                          ×
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
                {activeThread.kind === "dm" && (
                  <Box sx={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                    {QUICK_REPLIES.map(r => (
                      <Box key={r} component="button" onClick={() => setComposeText(r)} sx={{
                        height: 28, px: "10px", borderRadius: "999px", border: `1px solid ${T.line}`, bgcolor: "#fff",
                        fontFamily: "inherit", fontSize: "12.5px", color: T.ink, cursor: "pointer",
                        "&:hover": { bgcolor: T.surface3 },
                      }}>{r}</Box>
                    ))}
                  </Box>
                )}
                {activeThread.kind === "broadcast" && meRole !== "student" && (
                  <Box component="button" type="button" onClick={() => setAnnounce(a => !a)} aria-pressed={announce} sx={{
                    alignSelf: "flex-start", display: "inline-flex", alignItems: "center", gap: "6px", height: 30, px: "10px",
                    borderRadius: "var(--radius-md)", cursor: "pointer", fontFamily: "inherit", fontSize: "12.5px", fontWeight: 600,
                    border: `1px solid ${announce ? "var(--color-accent-violet)" : "transparent"}`,
                    bgcolor: announce ? "rgba(106,95,193,.08)" : "transparent",
                    color: announce ? "var(--color-accent-violet)" : T.ink3, "&:hover": { bgcolor: T.surface3 },
                  }}>
                    <MegaphoneIcon /> {announce ? `Announcement · emails all ${activeThread.memberCount ?? 0} members` : "Post as announcement"}
                  </Box>
                )}
                {activeThread.kind === "broadcast" && meRole !== "student" && announce && (
                  <Box component="label" sx={{
                    display: "inline-flex", alignItems: "center", gap: "8px",
                    alignSelf: "flex-start", ml: "52px", cursor: "pointer",
                    color: pinToDashboard ? T.teal : T.ink3,
                    fontSize: 12.5, fontWeight: 700,
                  }}>
                    <Box component="input"
                      type="checkbox"
                      checked={pinToDashboard}
                      onChange={(e) => setPinToDashboard(e.target.checked)}
                      sx={{ width: 14, height: 14, accentColor: T.teal, cursor: "pointer" }}
                    />
                    <Box sx={{ display: "inline-flex", alignItems: "center", gap: "5px" }}>
                      <PinIcon /> Pin to class dashboard
                    </Box>
                  </Box>
                )}
                <Box sx={{ display: "flex", gap: "10px", alignItems: "flex-end" }}>
                  <input ref={fileInputRef} type="file" multiple accept="*/*" style={{ display: "none" }} onChange={handleFilesSelected} />
                  <Box component="button" onClick={handlePickFiles} title="Attach file" sx={{ width: 42, height: 42, borderRadius: "11px", border: `1.5px solid ${T.line}`, bgcolor: "#fff", color: pendingAttachments.length > 0 ? T.primary : T.ink3, display: "grid", placeItems: "center", cursor: "pointer", flexShrink: 0, fontFamily: "inherit", transition: "all .12s", "&:hover": { bgcolor: T.surface3, color: T.ink } }}>
                    <PaperclipIcon />
                  </Box>
                  <Box
                    ref={composeRef}
                    component="textarea"
                    placeholder={activeThread?.kind === "broadcast" && activeThread.classCode
                      ? `Message #${activeThread.classCode}`
                      : activeThread?.placeholder || "Type a message…"}
                    value={composeText}
                    onChange={handleComposeChange}
                    onKeyDown={handleComposeKeydown}
                    sx={{
                      flex: 1, resize: "none", minHeight: 42, maxHeight: 120, height: 42,
                      border: `1.5px solid ${T.line}`, borderRadius: "12px",
                      p: "10px 14px", fontFamily: "inherit", fontSize: 14, color: T.ink,
                      outline: "none", transition: "border-color .14s, box-shadow .14s",
                      bgcolor: T.surface2, lineHeight: 1.4,
                      "&::placeholder": { color: T.ink4 },
                      "&:focus": { borderColor: T.primary, bgcolor: "#fff", boxShadow: `0 0 0 3px ${T.primary10}` },
                    }}
                  />
                  <Box sx={{ display: { xs: "none", md: "block" }, alignSelf: "center", fontSize: "11.5px", color: T.ink4, whiteSpace: "nowrap" }}>
                    Enter to send · Shift+Enter for a new line
                  </Box>
                  <Box
                    component="button"
                    disabled={!composeText.trim() && !pendingAttachments.some(p => p.uploadId && !p.error)}
                    onClick={sendMessage}
                    sx={{
                      width: 42, height: 42, borderRadius: "11px", flexShrink: 0,
                      bgcolor: T.primary, borderColor: T.primary, color: "var(--color-accent-lime)",
                      border: `1.5px solid ${T.primary}`,
                      display: "grid", placeItems: "center", cursor: "pointer",
                      fontFamily: "inherit", transition: "background .12s",
                      "&:hover:not(:disabled)": { bgcolor: T.primary700 },
                      "&:disabled": { opacity: 0.4, cursor: "not-allowed" },
                    }}
                  >
                    <SendIcon />
                  </Box>
                </Box>
              </Box>
            </>
          ) : (
            /* No thread selected — empty / loading state */
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "10px", color: T.ink4, fontSize: 14, p: "40px 32px", textAlign: "center" }}>
              {loadError
                ? <>
                    <Box sx={{ fontSize: "14px", fontWeight: 700, color: "#B42318" }}>Failed to load messages.</Box>
                    <Box component="button" onClick={() => window.location.reload()}
                      sx={{ color: "#B42318", fontWeight: 700, cursor: "pointer", bgcolor: "transparent", border: "none", textDecoration: "underline", fontFamily: "inherit", fontSize: 13 }}>
                      Retry
                    </Box>
                  </>
                : loading
                ? "Loading conversations…"
                : order.broadcast.length === 0 && order.dm.length === 0 && order.group.length === 0
                  ? <>
                      <Box sx={{ width: 48, height: 48, borderRadius: "14px", bgcolor: T.surface2, border: `1px solid ${T.line2}`, display: "grid", placeItems: "center", mb: "4px" }}>
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={T.ink4} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                      </Box>
                      <Box sx={{ fontSize: "15px", fontWeight: 700, color: T.ink3, fontFamily: "var(--font-display)" }}>No conversations yet</Box>
                      <Box sx={{ fontSize: "13px", color: T.ink4, maxWidth: 280, lineHeight: 1.6 }}>
                        Use the pencil button to start a message{meRole !== "student" ? ", or the megaphone to broadcast to a class" : ""}.
                      </Box>
                    </>
                  : <Box sx={{ fontSize: "13.5px", color: T.ink4 }}>Select a conversation to get started.</Box>
              }
            </Box>
          )}
        </Box>

        {/* ── RIGHT: Context panel ── */}
        <Box sx={{
          display: { xs: "none", md: contextCollapsed ? "none" : "flex" },
          flexDirection: "column", gap: "18px",
          borderLeft: `1px solid ${T.line}`, p: "18px",
          overflowY: "auto", minHeight: 0, bgcolor: T.surface,
        }}>
          {renderContextContent()}
        </Box>
      </Box>

      {/* ── New message modal ── */}
      <Modal show={showNewMsg} onClose={closeNewMessage} maxWidth={440}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "14px" }}>
          <Box sx={{ fontSize: "17px", fontWeight: 800, letterSpacing: "-.02em", color: T.ink, fontFamily: "var(--font-display)" }}>New Message</Box>
          <Box component="button" onClick={closeNewMessage} sx={{ width: 28, height: 28, borderRadius: "8px", border: 0, bgcolor: T.surface3, color: T.ink3, display: "grid", placeItems: "center", cursor: "pointer", "&:hover": { bgcolor: T.line, color: T.ink } }}>
            <XIcon />
          </Box>
        </Box>

        {/* Tab switcher — instructors/coordinators only */}
        {me.role !== "student" && (
          <Box sx={{ display: "flex", gap: "6px", mb: "14px", p: "3px", bgcolor: T.surface2, borderRadius: "11px", border: `1px solid ${T.line}` }}>
            {[
              { key: "dm", label: "Direct Message" },
              { key: "class", label: "Message by Class" },
            ].map(tab => (
              <Box key={tab.key} component="button" onClick={() => { setNmTab(tab.key); setNmSearch(""); setMsgClassStep(null); setRosterSelected(new Set()); setGroupError(null); setRosterError(null); setDmError(null); }}
                sx={{
                  flex: 1, height: 34, borderRadius: "8px", border: 0, cursor: "pointer",
                  fontFamily: "inherit", fontSize: "13px", fontWeight: 700, transition: "all .12s",
                  bgcolor: nmTab === tab.key ? "#fff" : "transparent",
                  color: nmTab === tab.key ? T.primary : T.ink3,
                  boxShadow: nmTab === tab.key ? T.shadowSm : "none",
                }}>
                {tab.label}
              </Box>
            ))}
          </Box>
        )}

        {/* DM tab */}
        {nmTab === "dm" && (
          <>
            {dmError && (
              <Box sx={{ color: "#B42318", fontSize: 13, mb: "8px", px: "4px" }}>{dmError}</Box>
            )}
            <Box sx={{ position: "relative", mb: "12px" }}>
              <Box sx={{ position: "absolute", left: "11px", top: "50%", transform: "translateY(-50%)", color: T.ink4, pointerEvents: "none", display: "flex" }}><SearchIcon /></Box>
              <Box component="input" type="text" placeholder="Search classmates and instructors"
                value={nmSearch} onChange={e => setNmSearch(e.target.value)}
                sx={{ width: "100%", height: 40, pl: "34px", pr: "12px", border: `1.5px solid ${T.line}`, borderRadius: "10px", bgcolor: T.surface2, fontFamily: "inherit", fontSize: "13.5px", color: T.ink, outline: "none", "&:focus": { borderColor: T.primary, bgcolor: "#fff", boxShadow: `0 0 0 3px ${T.primary10}` } }}
              />
            </Box>
            <Box sx={{ maxHeight: 320, overflowY: "auto", display: "flex", flexDirection: "column", gap: "2px" }}>
              {dirByRole.length === 0 && (
                <Box sx={{ p: "30px 10px", textAlign: "center", color: T.ink4, fontSize: 13 }}>
                  {nmSearch ? `No one matches "${nmSearch}"` : "No contacts available."}
                </Box>
              )}
              {dirByRole.map(g => (
                <React.Fragment key={g.role}>
                  <Box sx={{ fontSize: "10.5px", fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: T.ink4, pt: "10px", pb: "4px", px: "6px" }}>{g.label}</Box>
                  {g.people.map(p => (
                    <Box key={p.id} onClick={() => startConversation(p.id)} sx={{
                      display: "flex", alignItems: "center", gap: "11px",
                      p: "9px 8px", borderRadius: "10px", cursor: "pointer",
                      transition: "background .12s", "&:hover": { bgcolor: T.surface2 },
                    }}>
                      <AvatarEl name={p.name} role={p.role} size={36} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "7px" }}>
                          <Box sx={{ fontSize: "13.5px", fontWeight: 700, color: T.ink }}>{p.name}</Box>
                          <RoleBadge role={p.role} />
                        </Box>
                        <Box sx={{ fontSize: "12px", color: T.ink3, mt: "1px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.context}</Box>
                      </Box>
                      {p.threadId && threads[p.threadId] && (
                        <Box sx={{ fontSize: "10.5px", fontWeight: 700, color: T.teal, flexShrink: 0 }}>Continue</Box>
                      )}
                      <Box sx={{ color: T.ink4, flexShrink: 0 }}><ChevronRightIcon /></Box>
                    </Box>
                  ))}
                </React.Fragment>
              ))}
            </Box>
          </>
        )}

        {/* Message by Class tab — instructors/coordinators only */}
        {nmTab === "class" && me.role !== "student" && !msgClassStep && (
          <>
            <Box sx={{ mb: "8px", px: "2px", fontSize: "12.5px", color: T.ink3, lineHeight: 1.5 }}>
              Pick a class, then choose one or more students to message.
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", maxHeight: 320, overflowY: "auto" }}>
              {classes.length === 0 && (
                <Box sx={{ p: "30px 10px", textAlign: "center", color: T.ink4, fontSize: 13 }}>No assigned classes found.</Box>
              )}
              {classes.map(cls => (
                <Box key={cls.id} onClick={() => pickClassForMessage(cls.id)} sx={{
                  display: "flex", alignItems: "center", gap: "12px",
                  p: "12px 14px", borderRadius: "12px", cursor: "pointer",
                  border: `1.5px solid ${T.line}`, bgcolor: T.surface,
                  transition: "all .12s",
                  "&:hover": { bgcolor: T.surface2, borderColor: T.line2 },
                }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ fontSize: "14px", fontWeight: 700, color: T.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{cls.name}</Box>
                    <Box sx={{ fontSize: "12px", color: T.ink3, mt: "2px" }}>{cls.studentCount} students</Box>
                  </Box>
                  <Box sx={{ color: T.ink4, flexShrink: 0 }}><ChevronRightIcon /></Box>
                </Box>
              ))}
            </Box>
          </>
        )}

        {/* Message by Class — roster picker step */}
        {nmTab === "class" && me.role !== "student" && msgClassStep && (
          <>
            <Box component="button" onClick={() => { setMsgClassStep(null); setRosterError(null); }} sx={{
              display: "flex", alignItems: "center", gap: "6px", mb: "10px", px: "2px",
              border: 0, bgcolor: "transparent", color: T.ink3, fontFamily: "inherit",
              fontSize: "12.5px", fontWeight: 700, cursor: "pointer", "&:hover": { color: T.ink },
            }}>
              ← Back to classes
            </Box>
            {groupError && (
              <Box sx={{ color: "#B42318", fontSize: 13, mb: "8px", px: "4px" }}>{groupError}</Box>
            )}
            {rosterError && (
              <Box sx={{ color: "#B42318", fontSize: 13, mb: "8px", px: "4px" }}>
                {rosterError}{" "}
                <Box component="button" onClick={() => pickClassForMessage(msgClassStep)} sx={{ border: 0, bgcolor: "transparent", color: "#B42318", fontWeight: 700, textDecoration: "underline", cursor: "pointer", fontFamily: "inherit", fontSize: 13, p: 0 }}>Retry</Box>
              </Box>
            )}
            <Box sx={{ maxHeight: 280, overflowY: "auto", display: "flex", flexDirection: "column", gap: "2px" }}>
              {rosterLoading && (
                <Box sx={{ p: "30px 10px", textAlign: "center", color: T.ink4, fontSize: 13 }}>Loading roster…</Box>
              )}
              {!rosterLoading && !rosterError && (rosterCache[msgClassStep] || []).length === 0 && (
                <Box sx={{ p: "30px 10px", textAlign: "center", color: T.ink4, fontSize: 13 }}>No students in this class.</Box>
              )}
              {!rosterLoading && (rosterCache[msgClassStep] || []).map(s => {
                const checked = rosterSelected.has(s.id);
                return (
                  <Box key={s.id} onClick={() => toggleRosterStudent(s.id)} sx={{
                    display: "flex", alignItems: "center", gap: "11px",
                    p: "9px 8px", borderRadius: "10px", cursor: "pointer",
                    transition: "background .12s", "&:hover": { bgcolor: T.surface2 },
                  }}>
                    <AvatarEl name={s.name} role="student" size={32} />
                    <Box sx={{ flex: 1, minWidth: 0, fontSize: "13.5px", fontWeight: 700, color: T.ink }}>{s.name}</Box>
                    <Box sx={{
                      width: 18, height: 18, borderRadius: "5px", flexShrink: 0,
                      border: `1.5px solid ${checked ? T.primary : T.line}`,
                      bgcolor: checked ? T.primary : "transparent",
                      display: "grid", placeItems: "center", color: "#fff",
                    }}>
                      {checked && <CheckIcon />}
                    </Box>
                  </Box>
                );
              })}
            </Box>
            <Box component="button" onClick={confirmClassSelection} disabled={rosterSelected.size === 0 || groupCreating} sx={{
              width: "100%", height: 40, mt: "12px", borderRadius: "10px", border: 0,
              bgcolor: rosterSelected.size === 0 ? T.surface3 : T.primary,
              color: rosterSelected.size === 0 ? T.ink4 : "#fff",
              fontFamily: "inherit", fontSize: "13.5px", fontWeight: 700,
              cursor: rosterSelected.size === 0 || groupCreating ? "not-allowed" : "pointer",
              opacity: groupCreating ? 0.7 : 1,
            }}>
              {rosterSelected.size <= 1 ? "Message Student" : `Start Group Chat (${rosterSelected.size})`}
            </Box>
          </>
        )}
      </Modal>

      {/* ── New broadcast modal ── */}
      <Modal show={showBroadcastModal} onClose={() => setShowBroadcastModal(false)} maxWidth={440}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "14px" }}>
          <Box sx={{ fontSize: "17px", fontWeight: 800, letterSpacing: "-.02em", color: T.ink, fontFamily: "var(--font-display)" }}>New Broadcast</Box>
          <Box component="button" onClick={() => setShowBroadcastModal(false)} sx={{ width: 28, height: 28, borderRadius: "8px", border: 0, bgcolor: T.surface3, color: T.ink3, display: "grid", placeItems: "center", cursor: "pointer", "&:hover": { bgcolor: T.line, color: T.ink } }}>
            <XIcon />
          </Box>
        </Box>
        <Box sx={{ mb: "8px", px: "2px", fontSize: "12.5px", color: T.ink3, lineHeight: 1.5 }}>
          Select a class to open its broadcast channel. All students receive the message.
        </Box>
        {broadcastError && (
          <Box sx={{ color: "#B42318", fontSize: 13, mb: "8px", px: "4px" }}>{broadcastError}</Box>
        )}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", maxHeight: 360, overflowY: "auto" }}>
          {classes.length === 0 && (
            <Box sx={{ p: "30px 10px", textAlign: "center", color: T.ink4, fontSize: 13 }}>No assigned classes found.</Box>
          )}
          {classes.map(cls => {
            const hasThread = cls.threadId && threads[cls.threadId];
            return (
              <Box key={cls.id} onClick={async () => { await startBroadcast(cls.id); setShowBroadcastModal(false); }} sx={{
                display: "flex", alignItems: "center", gap: "12px",
                p: "12px 14px", borderRadius: "12px",
                cursor: broadcastSending ? "not-allowed" : "pointer",
                opacity: broadcastSending ? 0.6 : 1,
                border: `1.5px solid ${T.line}`, bgcolor: T.surface,
                transition: "all .12s",
                "&:hover": broadcastSending ? {} : { bgcolor: T.teal50, borderColor: T.teal100 },
              }}>
                <Box sx={{ width: 40, height: 40, borderRadius: "12px", bgcolor: T.teal, display: "grid", placeItems: "center", color: "#fff", flexShrink: 0 }}>
                  <MegaphoneIcon />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ fontSize: "14px", fontWeight: 700, color: T.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{cls.name}</Box>
                  <Box sx={{ fontSize: "12px", color: T.ink3, mt: "2px" }}>{cls.studentCount} students</Box>
                </Box>
                {hasThread
                  ? <Box sx={{ fontSize: "10.5px", fontWeight: 700, color: T.teal, flexShrink: 0 }}>Active</Box>
                  : <Box sx={{ fontSize: "10.5px", fontWeight: 700, color: T.ink4, flexShrink: 0 }}>New</Box>
                }
                <Box sx={{ color: T.ink4, flexShrink: 0 }}><ChevronRightIcon /></Box>
              </Box>
            );
          })}
        </Box>
      </Modal>

      {/* ── Mobile info modal ── */}
      <Modal show={showInfoModal} onClose={() => setShowInfoModal(false)} maxWidth={340}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: "4px" }}>
          <Box component="button" onClick={() => setShowInfoModal(false)} sx={{ width: 28, height: 28, borderRadius: "8px", border: 0, bgcolor: T.surface3, color: T.ink3, display: "grid", placeItems: "center", cursor: "pointer" }}><XIcon /></Box>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "18px" }}>
          {renderContextContent()}
        </Box>
      </Modal>

    </Box>
  );
}

// ── Thread group label ─────────────────────────────────────────────────────────
function DayDivider({ day }) {
  return (
    <Box sx={{
      display: "flex", alignItems: "center", gap: "12px", mx: { xs: "16px", md: "24px" }, mt: "18px", mb: "6px",
      "&::before, &::after": { content: '""', flex: 1, height: "1px", bgcolor: "var(--color-hairline-cloud)" },
    }}>
      <Box sx={{ fontSize: "10px", fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", color: "var(--color-ink-4)", whiteSpace: "nowrap" }}>{day}</Box>
    </Box>
  );
}

function ThreadGroupLabel({ children }) {
  return (
    <Box sx={{
      display: "flex", alignItems: "center", gap: "8px",
      px: "16px", pt: "14px", pb: "6px",
      fontSize: "10.5px", fontWeight: 700, letterSpacing: ".09em", textTransform: "uppercase", color: T.ink4,
      "&:first-of-type": { pt: "4px" },
    }}>
      {children}
      <Box sx={{ flex: 1, height: "1px", bgcolor: T.line2 }} />
    </Box>
  );
}
